import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';

const supportedLanguages = ['de', 'en', 'fr', 'ru', 'pl', 'ro'];

// Cache-Key fuer die Locale-Dateien — Begruendung ausfuehrlich am `loadPath`
// unten. Kurz: konstant innerhalb eines Release (Cache greift), neu bei jedem
// Deploy (keine veralteten Uebersetzungen).
const isProdBuildWithBuildId =
  process.env.NODE_ENV === 'production' && !!process.env.REACT_APP_BUILD_ID;

const localeCacheKey = isProdBuildWithBuildId
  ? process.env.REACT_APP_BUILD_ID
  : Date.now();

i18n
  .use(HttpApi)
  .use(LanguageDetector) 
  .use(initReactI18next)
  .init({
    supportedLngs: supportedLanguages,
    fallbackLng: 'de',
    debug: false, // Disable verbose i18next logging
    
    // KEIN `preload` (2026-08-12).
    //
    // Hier stand `preload: ['de','en','fr','ru','pl','ro']` mit der Begruendung
    // "avoid loading delays". Es bewirkte das Gegenteil: zusammen mit den 27
    // Namespaces unten ergab das 6 × 27 = 162 JSON-Requests bei JEDEM
    // Seitenaufruf — fuenf Sechstel davon fuer Sprachen, die der jeweilige
    // Nutzer nie zu sehen bekommt.
    //
    // Verschaerft durch den `?v=' + Date.now()`-Cache-Buster im `loadPath`
    // unten: der erzeugt pro Seitenaufruf neue URLs, also ist jeder dieser
    // Requests ein CDN-MISS bis zum Origin — obwohl die Dateien
    // `cache-control: max-age=3600` tragen. Gemessen gegen Produktion,
    // 6-fach parallel: 162 Requests, 1266 KB, 7767 ms. Alle 162 mit HTTP 200
    // — es ist kein Fehler, nur schiere Menge.
    //
    // Sichtbar wurde das im E2E-Nightly (PR #343): `useSuspense: false` laesst
    // die Seite VOR den Uebersetzungen rendern, der Anmelden-Button traegt
    // dann noch nicht sein Label. `getByRole('button', { name: /Anmelden/i })`
    // matcht also erst, wenn `de/auth.json` da ist — und das haengt in der
    // Schlange zwischen 161 anderen. Reale Nutzer zahlen denselben Preis bei
    // jedem kalten Seitenaufruf, nur beschwert sich dort niemand in einem Log.
    //
    // Ohne `preload` laedt i18next die erkannte Sprache plus `fallbackLng`
    // ('de') bei Bedarf: 27 Requests fuer deutsche Nutzer, 54 fuer alle
    // anderen. Die Cache-Semantik bleibt unangetastet — der Cache-Buster ist
    // bewusst NICHT Teil dieser Aenderung, der hat einen eigenen Trade-off
    // (Uebersetzungskorrekturen wuerden bis zu einer Stunde brauchen).

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
      lookupFromPathIndex: 0,
      lookupFromSubdomainIndex: 0,
    },
    
    interpolation: {
      escapeValue: false, // React already escapes
    },
    
    backend: {
      // CACHE-KEY PRO RELEASE STATT PRO SEITENAUFRUF (2026-08-12).
      //
      // Hier stand `?v=' + Date.now()`. Das wird beim Laden des Bundles EINMAL
      // ausgewertet, ist also pro Seitenaufruf neu — und damit ist jeder
      // Locale-Request eine URL, die weder Browser- noch CDN-Cache je gesehen
      // hat. Gemessen: `x-cache: MISS`, ausnahmslos. Die Dateien tragen
      // `cache-control: max-age=3600`, das lief bisher vollstaendig ins Leere.
      // `requestOptions.cache: 'no-cache'` (ebenfalls entfernt) erzwang
      // zusaetzlich eine Revalidierung, selbst wenn die URL mal stabil war.
      //
      // Der naheliegende Gegenvorschlag — den Parameter einfach streichen —
      // waere ein schlechter Tausch: dann greift zwar der Cache, aber eine
      // Uebersetzungskorrektur braeuchte bis zu einer Stunde, bis sie beim
      // Nutzer ankommt. Beides zusammen geht mit einem Wert, der INNERHALB
      // eines Release konstant ist und sich mit JEDEM Deploy aendert:
      //
      //   - waehrend eines Release: identische URL  -> Browser- und CDN-HIT
      //   - beim naechsten Deploy:  neue URL        -> sofort invalidiert
      //
      // `REACT_APP_BUILD_ID` setzt `deploy-app.yml` auf den Commit-SHA; CRA
      // backt den Wert beim Build ein. Ein Post-Build-Check im Workflow bricht
      // ab, wenn er fehlt — sonst wuerde die Verschlechterung still passieren.
      //
      // FEHLERRICHTUNG: Fehlt die Variable trotzdem (lokaler Build, Break-Glass
      // ueber deploy-webapp.ps1), faellt der Wert bewusst auf `Date.now()`
      // zurueck — also auf das alte, langsame, aber IMMER frische Verhalten.
      // Ein fester Fallback wie 'prod' waere die gefaehrlichere Variante: er
      // wuerde ueber Releases hinweg gleich bleiben und Nutzern dauerhaft alte
      // Uebersetzungen ausliefern. Lieber langsam als falsch.
      //
      // In der Entwicklung bleibt es absichtlich bei `Date.now()` plus
      // `no-cache`: wer an `public/locales/**` arbeitet, will die Aenderung
      // beim Reload sehen und nicht bis zu einer Stunde darauf warten.
      loadPath: `/locales/{{lng}}/{{ns}}.json?v=${localeCacheKey}`,
      ...(isProdBuildWithBuildId ? {} : { requestOptions: { cache: 'no-cache' as const } }),
    },
    
    // M-UX-0.1: 'homepage'-Namespace entfernt — war ausschließlich von der
    // gelöschten (ungerouteten) HomePage.tsx konsumiert (verifiziert per Grep).
    // RK-9: 'travel'-Namespace für das Dienstreisen-Feature (modules-reisekosten.md RK-9) ergänzt.
    ns: ['common', 'navigation', 'invoice', 'offer', 'auth', 'subscription', 'workflow', 'customer', 'legal', 'errors', 'seller', 'account', 'product', 'onboarding', 'openItems', 'payments', 'dunning', 'expenses', 'datev', 'ustva', 'gobd', 'inbound', 'empfangen', 'posteingang', 'bank', 'cockpit', 'travel'],
    defaultNS: 'common',
    
    react: {
      useSuspense: false, // Disable suspense to prevent loading issues
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      transEmptyNodeValue: '', // Return empty string for missing translations during development
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i'],
    },
    
    // Wait for i18next to be ready before rendering
    initImmediate: false,
    
    // Disable missing key saving in development to prevent POST errors
    saveMissing: false,
    
    // Compatibility options for older TypeScript
    compatibilityJSON: 'v3',
    cleanCode: true,
  });

export default i18n;